import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { CloseIcon } from '@/shared/ui/icons'
import { Button } from '@/shared/ui/Button'
import styles from './Payment.module.css'

interface Props {
  /** Что кодируем — ровно та строка, которую прочитает кошелёк. */
  value: string
  /** Есть ли в коде сумма. Если нет — говорим об этом вслух. */
  withAmount: boolean
  onClose: () => void
}

/**
 * QR отдельным окном, как в макете.
 *
 * Код рисуется локально на canvas: строка с адресом и суммой не должна
 * уезжать на чужой сервис-генератор. Уровень коррекции M — компромисс:
 * адреса Monero длинные, и на высоком уровне код становится настолько
 * плотным, что камера телефона мажет.
 */
export function QrModal({ value, withAmount, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    let cancelled = false
    QRCode.toCanvas(canvas, value, {
      width: 260,
      margin: 1,
      errorCorrectionLevel: 'M',
      color: { dark: '#111111', light: '#FFFFFF' },
    })
      .then(() => { if (!cancelled) setFailed(false) })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [value])

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="QR-код для оплаты"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.rowHead} style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h2 className={styles.modalTitle}>QR-код</h2>
            <p className={styles.modalSub}>
              {withAmount
                ? 'Отсканируйте QR-код для перевода.'
                : 'Сумму впишите вручную'}
            </p>
          </div>
          <Button variant="icon" onClick={onClose} aria-label="Закрыть"><CloseIcon /></Button>
        </div>

        <div className={styles.qrBox}>
          {failed ? (
            <p className={styles.qrFailed}>
              QR-код не отрисовался
            </p>
          ) : (
            <canvas ref={canvasRef} className={styles.qrCanvas} />
          )}
        </div>

        <Button block onClick={onClose}>Закрыть</Button>
      </div>
    </div>
  )
}
